/**
 * PublicShell — the shared marketing chrome (header + footer) for every
 * signed-out page that is NOT the homepage itself: About, Contact, Privacy,
 * Terms, Waitlist, and the auth pages when they want the full frame.
 *
 * Matches the homepage's SiteHeader/SiteFooter visually, but nav uses absolute
 * routes (`/#features`, `/about`, …) so the links work from any sub-page, not
 * just on-page anchors. One shell → the front door stays consistent instead of
 * ten bespoke headers.
 */

import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../ui/Button';
import { FigureCoWordmark } from '../brand/FigureCoLogo';
import { CARRIER_NOTE } from '../../config/seats.config';

const NAV_LINK_CLASS =
  'font-body text-sm font-medium tracking-tight text-text-secondary transition-colors hover:text-text-primary';

const FOOTER_LINK_CLASS =
  'font-body text-sm text-text-muted transition-colors hover:text-text-primary';

const NAV_LINKS: Array<{ label: string; to: string }> = [
  { label: "Who it's for", to: '/#features' },
  { label: 'Method', to: '/#architecture' },
  { label: 'About', to: '/about' },
];

export function PublicHeader() {
  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-hairline bg-base">
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4 sm:px-8 lg:px-12">
        <Link to="/" className="flex items-center" aria-label="The Figure Co. home">
          <FigureCoWordmark size="lg" />
        </Link>

        <ul className="hidden items-center gap-6 md:flex lg:gap-8">
          {NAV_LINKS.map((l) => (
            <li key={l.to}>
              <Link to={l.to} className={NAV_LINK_CLASS}>
                {l.label}
              </Link>
            </li>
          ))}
          <li>
            <Link to="/sign-in" className={NAV_LINK_CLASS}>
              Sign in
            </Link>
          </li>
          <li>
            <Button
              asChild
              className="!rounded-none !bg-brand font-mono text-xs font-semibold uppercase tracking-[0.14em] shadow-none hover:!bg-brand-hover"
              style={{ color: 'var(--color-on-brand)' }}
            >
              <Link to="/waitlist">Request early access</Link>
            </Button>
          </li>
        </ul>

        <div className="flex items-center md:hidden">
          <Button
            asChild
            size="sm"
            className="!rounded-none !bg-brand font-mono text-xs font-semibold uppercase tracking-[0.14em] shadow-none hover:!bg-brand-hover"
            style={{ color: 'var(--color-on-brand)' }}
          >
            <Link to="/waitlist">Request early access</Link>
          </Button>
        </div>
      </nav>
    </header>
  );
}

export function PublicFooter() {
  const { pathname } = useLocation();
  // Avoid the self-link when already on the carrier landing (K3): cross-link out.
  const audienceNote = pathname.startsWith('/for-carriers')
    ? { label: 'For attorneys →', href: '/' }
    : CARRIER_NOTE;
  return (
    <footer className="border-t border-hairline">
      <div className="mx-auto max-w-[1400px] px-5 py-12 sm:px-8 lg:px-12">
        <div className="mb-12 grid gap-10 md:grid-cols-4">
          <div>
            <div className="mb-4">
              <FigureCoWordmark size="sm" />
            </div>
            <p className="font-body text-sm text-text-muted">
              Forensic business-interruption claim calculations for attorneys,
              accountants, and the people who have to defend the number.
            </p>
          </div>
          <div>
            <h4 className="mb-4 font-body font-semibold text-text-primary">Product</h4>
            <ul className="space-y-2">
              <li><Link to="/#features" className={FOOTER_LINK_CLASS}>Who it is for</Link></li>
              <li><Link to="/#how-it-works" className={FOOTER_LINK_CLASS}>How it works</Link></li>
              <li><Link to="/#trust" className={FOOTER_LINK_CLASS}>Why the number holds</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="mb-4 font-body font-semibold text-text-primary">Company</h4>
            <ul className="space-y-2">
              <li><Link to="/about" className={FOOTER_LINK_CLASS}>About us</Link></li>
              <li><Link to="/contact" className={FOOTER_LINK_CLASS}>Contact</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="mb-4 font-body font-semibold text-text-primary">Legal</h4>
            <ul className="space-y-2">
              <li><Link to="/privacy" className={FOOTER_LINK_CLASS}>Privacy policy</Link></li>
              <li><Link to="/terms" className={FOOTER_LINK_CLASS}>Terms of service</Link></li>
            </ul>
          </div>
        </div>

        <p className="border-t border-hairline pt-8 font-mono text-[11px] leading-relaxed text-text-dim">
          Tamper-evident audit ledger · encrypted in transit · isolated per firm · never used to
          train AI · architected for SOC&nbsp;2 (certification on our roadmap).
        </p>
        <p className="mt-3 font-body text-[11px] leading-relaxed text-text-dim">
          The Figure Co. is a software platform — not a law firm or CPA firm. Every figure is
          attested by your own licensed professionals.
        </p>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-body text-sm text-text-dim">
            © {new Date().getFullYear()} The Figure Co. All rights reserved.
          </p>
          <Link
            to={audienceNote.href}
            className="font-mono text-xs text-text-dim transition-colors hover:text-text-secondary"
          >
            {audienceNote.label}
          </Link>
        </div>
      </div>
    </footer>
  );
}

/**
 * PublicShell — wraps page content between the shared header and footer.
 * `wide` opts out of the default prose max-width (e.g. a page that lays out
 * its own grid). Content gets top padding so it clears the fixed header.
 */
export default function PublicShell({
  children,
  wide = false,
}: {
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <div className="min-h-screen bg-base font-body">
      <PublicHeader />
      <main className="pt-24 sm:pt-28">
        {wide ? (
          children
        ) : (
          <div className="mx-auto max-w-3xl px-5 py-12 sm:px-8 lg:py-16">{children}</div>
        )}
      </main>
      <PublicFooter />
    </div>
  );
}
